import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  Modal,
  Spinner,
} from "react-bootstrap";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLeague } from "../hooks/useLeague";
import { useLeagueMembership } from "../context/LeagueMembershipContext";
import { useToast } from "../context/ToastContext";
import { useRequest } from "../hooks/useRequest";
import {
  Game,
  Goal,
  Penalty,
  Spare,
  Player,
  PenaltyType,
  Team,
} from "../types/models";
import {
  getGameUrl,
  getGoalsUrl,
  getGoalUrl,
  getPenaltiesUrl,
  getPenaltyUrl,
  getSparesUrl,
  getSpareUrl,
  getTeamPlayersUrl,
  getTeamUrl,
  getPenaltyTypesUrl,
} from "../config/api";
import { apiPatch, apiDelete } from "../utils/apiClient";
import { PaginatedResponse } from "../types/api";
import { formatGameDateTimeShort } from "../utils/dateUtils";
import GoalEntry from "./scoring/GoalEntry";
import PenaltyEntry from "./scoring/PenaltyEntry";
import SpareEntry from "./scoring/SpareEntry";
import {
  TeamRoster,
  buildAbsentCandidateRoster,
  buildTeamRoster,
} from "./scoring/players";
import { PERIODS, formatPeriod } from "./scoring/periods";
import { isNearStartTime } from "./scoring/liveWindow";
import GameStatusControl from "./scoring/GameStatusControl";
import { GameState } from "./scoring/gameStatus";
import ClockControl from "./scoring/ClockControl";
import { ClockUpdate } from "../utils/gameClock";
import {
  EventEmpty,
  EventGroup,
  EventRow,
  IconButton,
} from "./scoring/EventList";
import "./scoring/Scoreboard.css";

/** Which event the guided entry panel is capturing. */
type EntryKind = "goal" | "penalty" | "spare";

const ENTRY_KINDS: EntryKind[] = ["goal", "penalty", "spare"];

/**
 * The open panel is held in the URL rather than in component state, so the
 * browser's Back gesture — a back swipe on a phone — closes the panel instead
 * of leaving the scoreboard, and a refresh mid-entry reopens where it was.
 */
const ENTRY_PARAM = "entry";
const ENTRY_ID_PARAM = "id";

function parseEntryKind(value: string | null): EntryKind | null {
  return ENTRY_KINDS.find((kind) => kind === value) ?? null;
}

function withoutEntryParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(ENTRY_PARAM);
  next.delete(ENTRY_ID_PARAM);
  return next;
}

function GameScoringPage() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const { showToast } = useToast();
  const { canScoreGames, isLoading: isMembershipLoading } =
    useLeagueMembership();
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const [showScoreEdit, setShowScoreEdit] = useState(false);
  // Declining the prompt to go live holds for this visit to the scoreboard.
  const [livePromptDismissed, setLivePromptDismissed] = useState(false);
  const [isSavingState, setIsSavingState] = useState(false);
  const [isSavingClock, setIsSavingClock] = useState(false);

  const gameIdNum = parseInt(gameId || "0");

  const { data: game, error: gameError } = useRequest<Game>(
    `${getGameUrl(gameIdNum)}?_refresh=${refreshKey}`,
    auth.token,
  );

  const { data: goalsResponse, error: goalsError } = useRequest<
    PaginatedResponse<Goal>
  >(`${getGoalsUrl(gameIdNum)}?_refresh=${refreshKey}`, auth.token);

  const { data: penaltiesResponse, error: penaltiesError } = useRequest<
    PaginatedResponse<Penalty>
  >(`${getPenaltiesUrl(gameIdNum)}?_refresh=${refreshKey}`, auth.token);

  const { data: sparesResponse, error: sparesError } = useRequest<
    PaginatedResponse<Spare>
  >(`${getSparesUrl(gameIdNum)}?_refresh=${refreshKey}`, auth.token);

  const { data: homePlayersResponse, error: homePlayersError } = useRequest<
    PaginatedResponse<Player>
  >(game ? getTeamPlayersUrl(game.home_team_id) : undefined, auth.token);

  const { data: visitorPlayersResponse, error: visitorPlayersError } =
    useRequest<PaginatedResponse<Player>>(
      game ? getTeamPlayersUrl(game.visitor_team_id) : undefined,
      auth.token,
    );

  const { data: homeTeam, error: homeTeamError } = useRequest<Team>(
    game ? getTeamUrl(game.home_team_id) : undefined,
    auth.token,
  );

  const { data: visitorTeam, error: visitorTeamError } = useRequest<Team>(
    game ? getTeamUrl(game.visitor_team_id) : undefined,
    auth.token,
  );

  const { data: penaltyTypes, error: penaltyTypesError } = useRequest<
    PenaltyType[]
  >(
    getPenaltyTypesUrl(),
    auth.token,
    undefined,
    true, // Skip App-League header for global lookup
  );

  // Extract data arrays from paginated responses
  const goals = goalsResponse?.data;
  const penalties = penaltiesResponse?.data;
  const spares = sparesResponse?.data;
  const homePlayers = homePlayersResponse?.data;
  const visitorPlayers = visitorPlayersResponse?.data;

  // Which panel is open, and the record it edits, both read from the URL.
  const entryKind = parseEntryKind(searchParams.get(ENTRY_PARAM));
  const entryIdParam = searchParams.get(ENTRY_ID_PARAM);
  const entryId = entryIdParam === null ? null : Number(entryIdParam);

  const entryRecords: { id: number }[] | undefined =
    entryKind === "goal"
      ? goals
      : entryKind === "penalty"
        ? penalties
        : entryKind === "spare"
          ? spares
          : undefined;

  // An id that no longer resolves — the record was deleted, or the link is
  // stale — would otherwise open the panel as a brand new entry and invite a
  // duplicate. Fall back to the scoreboard instead.
  const hasStaleEntryId =
    entryId !== null &&
    entryRecords !== undefined &&
    !entryRecords.some((record) => record.id === entryId);

  useEffect(() => {
    if (!hasStaleEntryId) return;
    setSearchParams(withoutEntryParams, { replace: true });
  }, [hasStaleEntryId, setSearchParams]);

  // An entry panel takes the place of the scoreboard without moving the page,
  // so opening one from a button partway down leaves the scorekeeper scrolled
  // into the middle of a fresh form. Every panel starts at the top.
  useEffect(() => {
    if (entryKind === null) return;
    window.scrollTo(0, 0);
  }, [entryKind, entryId]);

  const refresh = () => {
    setRefreshKey(Date.now());
  };

  const openEntry = (kind: EntryKind, recordId?: number) => {
    const next = new URLSearchParams(searchParams);
    next.set(ENTRY_PARAM, kind);
    if (recordId == null) {
      next.delete(ENTRY_ID_PARAM);
    } else {
      next.set(ENTRY_ID_PARAM, String(recordId));
    }
    setSearchParams(next);
  };

  const closeEntry = () => {
    // Cancelling undoes the history entry that opening the panel added, so
    // Back from the scoreboard still reaches the schedule in one press. A
    // panel reached by link, or one that survived a refresh, has no
    // scoreboard entry behind it to return to.
    if (location.key === "default") {
      setSearchParams(withoutEntryParams, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const handleEntrySaved = () => {
    refresh();
    closeEntry();
  };

  // Offered when a scheduled game is around its start time, so the public
  // standings and schedule show it as live without the scorekeeper having to
  // remember. Recording a goal or penalty moves the game on its own; this
  // covers the stretch before the first one is entered.
  const shouldOfferGoLive =
    canScoreGames &&
    !livePromptDismissed &&
    game?.state === "scheduled" &&
    isNearStartTime(game.date);

  // The one path that writes a game's status, shared by the start-of-game
  // prompt and the status control in the header.
  const updateGameState = (state: GameState) => {
    if (!game) return;

    setIsSavingState(true);
    apiPatch(
      getGameUrl(game.id),
      { game: { state } },
      {
        token: auth.token,
        ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
      },
    )
      .then(() => {
        setIsSavingState(false);
        refresh();
      })
      .catch((error) => {
        showToast(
          error.message || "Could not update the game status.",
          "danger",
        );
        setIsSavingState(false);
      });
  };

  const handleGoLive = () => {
    setLivePromptDismissed(true);
    updateGameState("playing");
  };

  // Every clock action — play, pause, and setting the time and period — is the
  // same PATCH. The server works out what a paused clock read and stamps the
  // change itself, so nothing here computes a time.
  const updateGameClock = (changes: ClockUpdate) => {
    if (!game) return;

    setIsSavingClock(true);
    apiPatch(
      getGameUrl(game.id),
      { game: changes },
      {
        token: auth.token,
        ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
      },
    )
      .then(() => {
        setIsSavingClock(false);
        refresh();
      })
      .catch((error) => {
        showToast(error.message || "Could not update the clock.", "danger");
        setIsSavingClock(false);
      });
  };

  // Check for any errors
  const errorMessage = gameError
    ? `Error loading game: ${gameError.message}`
    : goalsError
      ? `Error loading goals: ${goalsError.message}`
      : penaltiesError
        ? `Error loading penalties: ${penaltiesError.message}`
        : sparesError
          ? `Error loading spares: ${sparesError.message}`
          : homePlayersError
            ? `Error loading home team players: ${homePlayersError.message}`
            : visitorPlayersError
              ? `Error loading visitor team players: ${visitorPlayersError.message}`
              : homeTeamError
                ? `Error loading home team: ${homeTeamError.message}`
                : visitorTeamError
                  ? `Error loading visitor team: ${visitorTeamError.message}`
                  : penaltyTypesError
                    ? `Error loading penalty types: ${penaltyTypesError.message}`
                    : null;

  if (errorMessage) {
    return (
      <Container fluid className="px-0">
        <Alert variant="danger">{errorMessage}</Alert>
      </Container>
    );
  }

  // Goals, penalties and spares are awaited too: the entry panel resolves the
  // record it edits out of these lists, so rendering before they land would
  // turn an edit into a new entry.
  if (
    isMembershipLoading ||
    !game ||
    !goals ||
    !penalties ||
    !spares ||
    !homePlayers ||
    !visitorPlayers ||
    !homeTeam ||
    !visitorTeam ||
    !penaltyTypes
  ) {
    return (
      <Container fluid className="px-0">
        <Spinner animation="border" /> Loading...
      </Container>
    );
  }

  // The API policies are the authority on this, but a member who cannot score
  // should not be shown a scoreboard whose every action would be refused.
  if (!canScoreGames) {
    return (
      <Container fluid className="px-0">
        <Alert variant="warning">
          You do not have permission to score this game. Ask a league owner or
          admin for scorekeeper access.
        </Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Back to Schedule
        </Button>
      </Container>
    );
  }

  const homeTeamName = homeTeam.name;
  const visitorTeamName = visitorTeam.name;
  const matchup = `${visitorTeamName} @ ${homeTeamName}`;

  const allPlayers = [...homePlayers, ...visitorPlayers];

  const rosters: TeamRoster[] = [
    buildTeamRoster(
      game.visitor_team_id,
      visitorTeamName,
      visitorPlayers,
      spares,
    ),
    buildTeamRoster(game.home_team_id, homeTeamName, homePlayers, spares),
  ];

  // Who can still be marked absent, which excludes anyone already recorded as
  // such apart from the player the spare being edited already replaces.
  const absentRosters = (editing: Spare | null): TeamRoster[] => {
    const keepPlayerId = editing?.replaced_player_id ?? null;

    return [
      buildAbsentCandidateRoster(
        game.visitor_team_id,
        visitorTeamName,
        visitorPlayers,
        spares,
        keepPlayerId,
      ),
      buildAbsentCandidateRoster(
        game.home_team_id,
        homeTeamName,
        homePlayers,
        spares,
        keepPlayerId,
      ),
    ];
  };

  // Goal, penalty and spare entry take the place of the scoreboard rather
  // than overlaying it, so the roster and the keypad get the whole screen on
  // a phone and the page scrolls normally.
  if (entryKind && !hasStaleEntryId) {
    const editingGoal =
      entryId === null
        ? null
        : goals.find((goal) => goal.id === entryId) ?? null;
    const editingPenalty =
      entryId === null
        ? null
        : penalties.find((penalty) => penalty.id === entryId) ?? null;
    const editingSpare =
      entryId === null
        ? null
        : spares.find((spare) => spare.id === entryId) ?? null;

    return (
      <Container fluid className="px-0">
        {entryKind === "goal" ? (
          <GoalEntry
            game={game}
            subtitle={matchup}
            rosters={rosters}
            editingGoal={editingGoal}
            onSaved={handleEntrySaved}
            onCancel={closeEntry}
          />
        ) : entryKind === "penalty" ? (
          <PenaltyEntry
            game={game}
            subtitle={matchup}
            rosters={rosters}
            penaltyTypes={penaltyTypes}
            editingPenalty={editingPenalty}
            onSaved={handleEntrySaved}
            onCancel={closeEntry}
          />
        ) : (
          <SpareEntry
            game={game}
            subtitle={matchup}
            rosters={absentRosters(editingSpare)}
            editingSpare={editingSpare}
            onSaved={handleEntrySaved}
            onCancel={closeEntry}
          />
        )}
      </Container>
    );
  }

  return (
    <Container fluid className="px-0">
      <div className="sb-header">
        <IconButton
          icon="arrow-left"
          label="Back to schedule"
          onClick={() => navigate(-1)}
        />
        <h1 className="sb-title">Game Scoreboard</h1>
      </div>

      {/* Game Info Section */}
      <Card className="sb-card mb-3">
        <Card.Body>
          <div className="sb-gameinfo-matchup">{matchup}</div>
          <div className="sb-gameinfo-meta">
            <span className="sb-gameinfo-date">
              {formatGameDateTimeShort(game.date)}
            </span>
            <GameStatusControl
              state={game.state}
              isSaving={isSavingState}
              onChange={updateGameState}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Score Section */}
      <Card className="sb-card mb-3">
        <Card.Body>
          <ScoreDisplay
            game={game}
            homeTeamName={homeTeamName}
            visitorTeamName={visitorTeamName}
            showEdit={showScoreEdit}
            setShowEdit={setShowScoreEdit}
            onUpdate={refresh}
            clock={
              <ClockControl
                game={game}
                periodMinutes={currentLeague?.period_minutes}
                isSaving={isSavingClock}
                onChange={updateGameClock}
              />
            }
          />
        </Card.Body>
      </Card>

      <Modal
        show={shouldOfferGoLive}
        onHide={() => setLivePromptDismissed(true)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Start this game?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {matchup} is scheduled for {formatGameDateTimeShort(game.date)}. Mark
          it as in progress so it shows as live on the schedule and standings?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setLivePromptDismissed(true)}
            disabled={isSavingState}
          >
            Not yet
          </Button>
          <Button
            variant="primary"
            onClick={handleGoLive}
            disabled={isSavingState}
          >
            {isSavingState ? "Starting..." : "Yes, it's live"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Main Action Buttons */}
      <div className="sb-actions">
        <Button size="lg" variant="primary" onClick={() => openEntry("goal")}>
          Add Goal
        </Button>
        <Button
          size="lg"
          variant="warning"
          onClick={() => openEntry("penalty")}
        >
          Add Penalty
        </Button>
        {/* Named for the change rather than the substitute: a player can be
            marked absent with nobody coming in for them. */}
        <Button
          size="lg"
          variant="outline-secondary"
          onClick={() => openEntry("spare")}
        >
          Lineup Change
        </Button>
      </div>

      {/* Goals Section */}
      <div className="sb-grid">
        <Card className="sb-card">
          <Card.Header>
            <strong>{visitorTeamName} Goals</strong>
          </Card.Header>
          <Card.Body>
            <GoalsList
              goals={goals.filter((g) => g.team_id === game.visitor_team_id)}
              allPlayers={allPlayers}
              onEdit={(goal) => openEntry("goal", goal.id)}
              onDelete={refresh}
            />
          </Card.Body>
        </Card>
        <Card className="sb-card">
          <Card.Header>
            <strong>{homeTeamName} Goals</strong>
          </Card.Header>
          <Card.Body>
            <GoalsList
              goals={goals.filter((g) => g.team_id === game.home_team_id)}
              allPlayers={allPlayers}
              onEdit={(goal) => openEntry("goal", goal.id)}
              onDelete={refresh}
            />
          </Card.Body>
        </Card>
      </div>

      {/* Penalties Section */}
      <div className="sb-grid">
        <Card className="sb-card">
          <Card.Header>
            <strong>{visitorTeamName} Penalties</strong>
          </Card.Header>
          <Card.Body>
            <PenaltiesList
              penalties={penalties.filter(
                (p) => p.team_id === game.visitor_team_id,
              )}
              allPlayers={allPlayers}
              penaltyTypes={penaltyTypes}
              onEdit={(penalty) => openEntry("penalty", penalty.id)}
              onDelete={refresh}
            />
          </Card.Body>
        </Card>
        <Card className="sb-card">
          <Card.Header>
            <strong>{homeTeamName} Penalties</strong>
          </Card.Header>
          <Card.Body>
            <PenaltiesList
              penalties={penalties.filter(
                (p) => p.team_id === game.home_team_id,
              )}
              allPlayers={allPlayers}
              penaltyTypes={penaltyTypes}
              onEdit={(penalty) => openEntry("penalty", penalty.id)}
              onDelete={refresh}
            />
          </Card.Body>
        </Card>
      </div>

      {/* Lineup Changes Section */}
      <h2 className="sb-section-title">Lineup Changes</h2>
      <div className="sb-grid">
        <Card className="sb-card">
          <Card.Header>
            <strong>{visitorTeamName}</strong>
          </Card.Header>
          <Card.Body>
            <SparesList
              spares={spares.filter((s) => s.team_id === game.visitor_team_id)}
              onEdit={(spare) => openEntry("spare", spare.id)}
              onDelete={refresh}
            />
          </Card.Body>
        </Card>
        <Card className="sb-card">
          <Card.Header>
            <strong>{homeTeamName}</strong>
          </Card.Header>
          <Card.Body>
            <SparesList
              spares={spares.filter((s) => s.team_id === game.home_team_id)}
              onEdit={(spare) => openEntry("spare", spare.id)}
              onDelete={refresh}
            />
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
}

// Score Display Component
function ScoreDisplay({
  game,
  homeTeamName,
  visitorTeamName,
  showEdit,
  setShowEdit,
  onUpdate,
  clock,
}: {
  game: Game;
  homeTeamName: string;
  visitorTeamName: string;
  showEdit: boolean;
  setShowEdit: (show: boolean) => void;
  onUpdate: () => void;
  /** The game clock, shown between the two scores. */
  clock?: React.ReactNode;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage("");

    const form = event.currentTarget;
    const homeScore = parseInt(
      (form.elements.namedItem("homeScore") as HTMLInputElement).value,
    );
    const visitorScore = parseInt(
      (form.elements.namedItem("visitorScore") as HTMLInputElement).value,
    );

    if (isNaN(homeScore) || isNaN(visitorScore)) {
      setErrorMessage("Please enter valid scores");
      return;
    }

    setIsSubmitting(true);

    apiPatch(
      getGameUrl(game.id),
      {
        game: {
          home_score: homeScore,
          visitor_score: visitorScore,
        },
      },
      {
        token: auth.token,
        ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
      },
    )
      .then(() => {
        setShowEdit(false);
        onUpdate();
      })
      .catch((error) => {
        setErrorMessage(error.message || "An error occurred");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (showEdit) {
    return (
      <div>
        <h5>Edit Score</h5>
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        <Form onSubmit={handleSubmit}>
          <div className="sb-score-fields">
            <Form.Group className="sb-score-field">
              <Form.Label>{visitorTeamName} (Away)</Form.Label>
              <Form.Control
                type="number"
                name="visitorScore"
                defaultValue={game.visitor_score ?? 0}
                min="0"
                required
                disabled={isSubmitting}
                className="sb-score-input"
              />
            </Form.Group>
            <Form.Group className="sb-score-field">
              <Form.Label>{homeTeamName} (Home)</Form.Label>
              <Form.Control
                type="number"
                name="homeScore"
                defaultValue={game.home_score ?? 0}
                min="0"
                required
                disabled={isSubmitting}
                className="sb-score-input"
              />
            </Form.Group>
          </div>
          <p className="sb-score-hint">
            Adding or removing a goal sets the score on its own. Enter it here
            only for a game you are not recording goal by goal — anything typed
            in is replaced the next time a goal changes.
          </p>
          <Button
            variant="secondary"
            onClick={() => setShowEdit(false)}
            disabled={isSubmitting}
            className="me-2"
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Form>
      </div>
    );
  }

  return (
    <div>
      {/* A three-column grid rather than centred absolutely: the clock gets
          its own track, so it cannot overlap either score however narrow the
          screen gets or however long a team's name is. */}
      <div className="sb-score-teams">
        <div className="sb-score-side">
          <div className="sb-score-team">{visitorTeamName}</div>
          <div className="sb-score-value">{game.visitor_score ?? 0}</div>
          <div className="sb-score-role">Away</div>
        </div>
        {clock && <div className="sb-score-clock">{clock}</div>}
        <div className="sb-score-side">
          <div className="sb-score-team">{homeTeamName}</div>
          <div className="sb-score-value">{game.home_score ?? 0}</div>
          <div className="sb-score-role">Home</div>
        </div>
      </div>
      {/* Centred beneath both scores — as a middle column it overlapped the
          home team's score on a narrow screen. */}
      <div className="sb-score-edit">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => setShowEdit(true)}
        >
          <i className="bi bi-pencil me-1" aria-hidden="true" />
          Edit Score
        </Button>
      </div>
    </div>
  );
}

// Goals List Component
function GoalsList({
  goals,
  allPlayers,
  onEdit,
  onDelete,
}: {
  goals: Goal[];
  allPlayers: Player[];
  onEdit: (goal: Goal) => void;
  onDelete: () => void;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();

  const handleDelete = (goalId: number) => {
    if (!window.confirm("Are you sure you want to delete this goal?")) return;

    apiDelete(getGoalUrl(goalId), {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then(() => {
        onDelete();
      })
      .catch((error) => {
        alert(error.message || "An error occurred while deleting the goal");
      });
  };

  const getPlayerName = (playerId: number) => {
    const player = allPlayers.find((p) => p.id === playerId);
    if (!player) return `Player ${playerId}`;
    return `#${player.jersey || "-"} ${player.last_name}, ${player.first_name}`;
  };

  if (goals.length === 0) {
    return <EventEmpty>No goals recorded yet.</EventEmpty>;
  }

  return (
    <div>
      {PERIODS.map((period) => {
        const periodGoals = goals.filter((g) => g.period === period);
        if (periodGoals.length === 0) return null;

        return (
          <EventGroup key={period} title={formatPeriod(period)}>
            {periodGoals.map((goal) => {
              const goalScorer = goal.goal_player_name
                ? `#${goal.goal_player_jersey || "-"} ${goal.goal_player_name}`
                : getPlayerName(goal.goal_player_id);

              const assists = [];
              if (goal.assist1_player_name) {
                assists.push(
                  `#${goal.assist1_player_jersey || "-"} ${
                    goal.assist1_player_name
                  }`,
                );
              }
              if (goal.assist2_player_name) {
                assists.push(
                  `#${goal.assist2_player_jersey || "-"} ${
                    goal.assist2_player_name
                  }`,
                );
              }

              return (
                <EventRow
                  key={goal.id}
                  lead={goal.time_formatted || goal.time}
                  primary={goalScorer}
                  secondary={
                    assists.length > 0
                      ? `Assists: ${assists.join(", ")}`
                      : undefined
                  }
                  onEdit={() => onEdit(goal)}
                  onDelete={() => handleDelete(goal.id)}
                  editLabel={`Edit goal by ${goalScorer}`}
                  deleteLabel={`Delete goal by ${goalScorer}`}
                />
              );
            })}
          </EventGroup>
        );
      })}
    </div>
  );
}

// Penalties List Component
function PenaltiesList({
  penalties,
  allPlayers,
  penaltyTypes,
  onEdit,
  onDelete,
}: {
  penalties: Penalty[];
  allPlayers: Player[];
  penaltyTypes: PenaltyType[];
  onEdit: (penalty: Penalty) => void;
  onDelete: () => void;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();

  const handleDelete = (penaltyId: number) => {
    if (!window.confirm("Are you sure you want to delete this penalty?"))
      return;

    apiDelete(getPenaltyUrl(penaltyId), {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then(() => {
        onDelete();
      })
      .catch((error) => {
        alert(error.message || "An error occurred while deleting the penalty");
      });
  };

  const getPlayerName = (playerId: number) => {
    const player = allPlayers.find((p) => p.id === playerId);
    if (!player) return `Player ${playerId}`;
    return `#${player.jersey || "-"} ${player.last_name}, ${player.first_name}`;
  };

  const getPenaltyTypeName = (penaltyTypeId: number) => {
    const penaltyType = penaltyTypes.find((pt) => pt.id === penaltyTypeId);
    return penaltyType ? penaltyType.name : `Type ${penaltyTypeId}`;
  };

  if (penalties.length === 0) {
    return <EventEmpty>No penalties recorded yet.</EventEmpty>;
  }

  return (
    <div>
      {PERIODS.map((period) => {
        const periodPenalties = penalties.filter((p) => p.period === period);
        if (periodPenalties.length === 0) return null;

        return (
          <EventGroup key={period} title={formatPeriod(period)}>
            {periodPenalties.map((penalty) => {
              const player = penalty.penalty_player_name
                ? `#${penalty.penalty_player_jersey || "-"} ${
                    penalty.penalty_player_name
                  }`
                : getPlayerName(penalty.penalty_player_id);

              return (
                <EventRow
                  key={penalty.id}
                  lead={penalty.off_time_formatted || penalty.off_time}
                  primary={player}
                  secondary={`${getPenaltyTypeName(
                    penalty.penalty_type_id,
                  )} · ${penalty.penalty_length} min`}
                  onEdit={() => onEdit(penalty)}
                  onDelete={() => handleDelete(penalty.id)}
                  editLabel={`Edit penalty to ${player}`}
                  deleteLabel={`Delete penalty to ${player}`}
                />
              );
            })}
          </EventGroup>
        );
      })}
    </div>
  );
}

// Spares List Component
function SparesList({
  spares,
  onEdit,
  onDelete,
}: {
  spares: Spare[];
  onEdit: (spare: Spare) => void;
  onDelete: () => void;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();

  const handleDelete = (spareId: number) => {
    if (!window.confirm("Are you sure you want to delete this spare record?"))
      return;

    apiDelete(getSpareUrl(spareId), {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then(() => {
        onDelete();
      })
      .catch((error) => {
        alert(error.message || "An error occurred while deleting the spare");
      });
  };

  if (spares.length === 0) {
    return <EventEmpty>No lineup changes recorded yet.</EventEmpty>;
  }

  return (
    <div>
      {spares.map((spare) => {
        const absentPlayer = spare.replaced_player_name
          ? `#${spare.replaced_player_jersey || "-"} ${
              spare.replaced_player_name
            }`
          : `Player ${spare.replaced_player_id}`;

        const substitutePlayer = spare.spare_player_name
          ? `#${spare.spare_player_jersey || "-"} ${spare.spare_player_name}`
          : spare.spare_player_id
            ? `Player ${spare.spare_player_id}`
            : null;

        const jerseyNote = spare.jersey ? ` (wearing #${spare.jersey})` : "";

        return (
          <EventRow
            key={spare.id}
            primary={absentPlayer}
            secondary={
              substitutePlayer
                ? `Replaced by ${substitutePlayer}${jerseyNote}`
                : "Absent — no replacement"
            }
            onEdit={() => onEdit(spare)}
            onDelete={() => handleDelete(spare.id)}
            editLabel={`Edit lineup change for ${absentPlayer}`}
            deleteLabel={`Delete lineup change for ${absentPlayer}`}
          />
        );
      })}
    </div>
  );
}

export default GameScoringPage;
